import { useMemo, useState } from "react";
import type { DatabookSheetPreview } from "@/lib/api/databook";
import { MappingColumnFilter } from "./MappingColumnFilter";
import { availableMappingFilterValues, filterMappingSheet, mappingFilterModel, type MappingFilterSelection } from "./mappingFilters";

const EMPTY_FILTERS: MappingFilterSelection = new Map();

export function useMappingFilters(sheet: DatabookSheetPreview | null, scope: string, clearSelection: () => void) {
  const [state, setState] = useState({ scope, filters: EMPTY_FILTERS });
  const filters = state.scope === scope ? state.filters : EMPTY_FILTERS;
  const model = useMemo(() => mappingFilterModel(sheet), [sheet]);
  const filteredSheet = useMemo(() => sheet && model ? filterMappingSheet(sheet, model, filters) : sheet, [sheet, model, filters]);
  const controls = useMemo(() => {
    if (!model || !sheet || !filteredSheet) return undefined;
    const reset = () => {
      clearSelection();
      setState({ scope, filters: EMPTY_FILTERS });
    };
    return {
      headerRow: model.headerRow,
      actions: new Map(model.columns.map(column => [column.index,
        <MappingColumnFilter key={`${scope}:${column.index}`} column={column}
          availableValues={availableMappingFilterValues(sheet, model, filters, column)}
          selected={filters.get(column.index)} onApply={values => {
          clearSelection();
          const next = new Map(filters);
          if (values === null) next.delete(column.index); else next.set(column.index, values);
          setState({ scope, filters: next });
        }} />,
      ])),
      active: filters.size > 0,
      totalRows: sheet.rows.filter(row => row.index > model.headerRow).length,
      visibleRows: filteredSheet.rows.filter(row => row.index > model.headerRow).length,
      reset,
    };
  }, [model, sheet, filteredSheet, filters, scope, clearSelection]);
  return { sheet: filteredSheet, controls };
}

export type MappingFilterControls = ReturnType<typeof useMappingFilters>["controls"];
